import { DynamicForm, SelectOption } from '@/components/form-builder/form-builder.types';
import { getFormBuilderObjectFactory } from '@/components/form-builder/utils/form-builder-factory';
import { Component, inject, OnInit } from '@angular/core';
import { getContractingAuthorityFormObject } from '../utils/contracting-authority-form-object';
import { ContractingAuthorityRepository } from '@/store/contracting-authority-state/contracting-authority-state.repository';
import { ContractingAuthorityService } from '../contracting-authority.service';
import { MessageService } from 'primeng/api';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { PageHeaderComponent } from '../../../../components/shared/page-header/page-header.component';
import { FormBuilderComponent } from '../../../../components/form-builder/form-builder.component';
import { TabsModule } from 'primeng/tabs';
import { MembersComponent } from '../../members/members.component';
import { RefParamsService } from '../../ref-params/ref-params.service';
import { RefTypeEnum } from '@/types';
import { forkJoin, BehaviorSubject } from 'rxjs';
import { SettingsSeuilsComponent } from '../../settings-seuils/settings-seuils.component';
import { CommonModule } from '@angular/common';
import { ButtonModule } from 'primeng/button';

@Component({
  selector: 'sm-contracting-authority-details',
  imports: [
    PageHeaderComponent,
    FormBuilderComponent,
    TabsModule,
    MembersComponent,
    TranslateModule,
    CommonModule,
    SettingsSeuilsComponent,
    ButtonModule,
  ],
  templateUrl: './contracting-authority-details.component.html',
  styleUrl: './contracting-authority-details.component.scss',
})
export class ContractingAuthorityDetailsComponent implements OnInit {
  refParamsService = inject(RefParamsService);
  contractingAuthorityRepository = inject(ContractingAuthorityRepository);
  contractingAuthorityService = inject(ContractingAuthorityService);
  messageService = inject(MessageService);
  activatedRoute = inject(ActivatedRoute);
  router = inject(Router);
  translate = inject(TranslateService);

  breadcrumb = [
    {
      label: 'settings.contractingAuthority.breadcrumb.parametres',
      routerLink: ['/settings'],
    },
    {
      label: 'settings.contractingAuthority.breadcrumb.authorities',
      routerLink: ['/settings', 'contracting-authority'],
    },
    {
      label: 'settings.contractingAuthority.breadcrumb.consult',
      routerLink: ['/settings', 'contracting-authority', 'details'],
    },
  ];

  selectedTab = 'members';
  id = 0;
  logoUrl = '';

  formObject: DynamicForm = getFormBuilderObjectFactory();

  tuttelOptions: SelectOption[] = [];
  comOptions: SelectOption[] = [];
  paysOptions: SelectOption[] = [];
  commissionTypeOptions: { name: string; code: string }[] = [
    { name: 'TUTELLE', code: 'TUTELLE' },
    { name: 'INTERNE', code: 'INTERNE' },
    { name: 'AUTRE', code: 'AUTRE' },
  ];
  commissionOptions$ = new BehaviorSubject<{ name: string; code: number }[]>([]);

  ngOnInit() {
    this.initRouting();

    // Load both options and data together, then create form object
    forkJoin({
      tutels: this.contractingAuthorityService.getTutels(),
      coms: this.contractingAuthorityService.getComs({ size: 1000 }),
      pays: this.refParamsService.getSpecializedRefParams(RefTypeEnum.COUNTRY),
      data: this.contractingAuthorityService.getOneContractingAuthorityBatch(this.id),
    }).subscribe({
      next: ({ tutels, coms, pays, data }) => {
        this.tuttelOptions = tutels.map(ref => ({ name: ref.nom, code: ref.id }));
        this.comOptions = coms.content.map((com: any) => ({ name: com.nom, code: com.id }));
        this.paysOptions = pays.map((p: any) => ({ name: p.description, code: p.id }));

        // Set all the data first
        this.contractingAuthorityRepository.set('response', data);
        this.contractingAuthorityRepository.set('dto', {
          nom: data.nom,
          abreviation: data.abreviation ?? '',
          nomAr: data.nomAr ?? '',
          tutelId: data.tutel?.id,
          comId: data.com?.id !== undefined ? data.com.id : undefined,
          email: data.email ?? '',
          logo: data.logo ?? '',
          logoFile: undefined, // Will be set when user selects a file
          istTutel: data.istTutel ?? false,
          contact: data.contact ?? '',
          userIds: data.users?.map(user => user.id.toString()) || [],
          ancrage: data.ancrage ?? '',
          typeAffCommission: data.typeAffCommission ?? null,
          adresse: {
            id: data.adresse?.id,
            tel1: data.adresse?.tel1 ?? '',
            tel2: data.adresse?.tel2 ?? '',
            fax: data.adresse?.fax ?? '',
            adresse: data.adresse?.adresse ?? '',
            compAdresse: data.adresse?.compAdresse ?? '',
            codePostal: data.adresse?.codePostal ?? '',
            ville: data.adresse?.ville ?? '',
            paysId: data.adresse?.pays && typeof (data.adresse.pays as any).id === 'number' ? (data.adresse.pays as any).id : undefined,
          },
        });

        // Load logo URL if logo exists
        if (data.logo) {
          this.contractingAuthorityService.getAcPhoto(data.id).subscribe({
            next: imageData => {
              this.logoUrl = `data:image/${imageData.type};base64,${imageData.content}`;
            },
            error: () => {
              this.logoUrl = '';
            },
          });
        }

        this.contractingAuthorityRepository.set('tuttelSelected', data.tutel ? { name: data.tutel.nom, code: data.tutel.id } : undefined);
        this.contractingAuthorityRepository.set('comSelected', data.com ? { name: data.com.nom, code: data.com.id } : undefined);
        this.contractingAuthorityRepository.set(
          'paysSelected',
          data.adresse?.pays && typeof (data.adresse.pays as any).id === 'number'
            ? { name: data.adresse.pays.description, code: (data.adresse.pays as any).id }
            : undefined
        );

        // Handle commission selection from API response FIRST
        if (data.com) {
          this.contractingAuthorityRepository.setNested('dto', 'comId', data.com.id);
          this.contractingAuthorityRepository.set('comSelected', { name: data.com.nom, code: data.com.id });
        }

        // Handle commission type from API response
        if (data.typeAffCommission) {
          this.contractingAuthorityRepository.setNested('dto', 'typeAffCommission', data.typeAffCommission);
          // Load commission options based on the type and then create form object
          this.loadCommissionOptionsAndCreateForm(data.typeAffCommission);
        } else {
          // No commission type, create form object immediately
          this.createFormObject();
        }
      },
      error: () => {
        this.messageService.add({
          severity: 'error',
          summary: 'Erreur',
          detail: this.translate.instant('settings.contractingAuthority.ac.message.error.loading'),
        });
      },
    });

    this.contractingAuthorityRepository.setNested('isDisabled', {
      nom: true,
      abreviation: true,
      nomAr: true,
      tutel: true,
      contact: true,
      email: true,
      istTutel: true,
      comId: true,
      adresse: true,
    });
  }

  initRouting() {
    this.activatedRoute.fragment.subscribe(fragment => {
      if (fragment !== null && ['0', '1', '2', '3', '4', '5'].includes(fragment)) {
        this.selectedTab = fragment;
      }
    });

    this.id = Number(this.activatedRoute.snapshot.paramMap.get('id'));
  }

  onTabChange(event: any) {
    this.selectedTab = event;
    this.router.navigate([], {
      fragment: event,
      replaceUrl: true,
    });
  }

  getCommissionTypeId(typeCommission: string): string {
    switch (typeCommission) {
      case 'TUTELLE':
        return 'TUTELLE';
      case 'INTERNE':
        return 'INTERNE';
      case 'AUTRE':
        return 'AUTRE';
      default:
        return 'AUTRE';
    }
  }

  loadCommissionOptions(commissionTypeId: string) {
    let apiCall: any;
    const currentComId = this.contractingAuthorityRepository.getNested<number>('dto', 'comId');
    const currentComSelected = this.contractingAuthorityRepository.get('comSelected');

    switch (commissionTypeId) {
      case 'TUTELLE': // Commission Tutelle
        apiCall = this.contractingAuthorityService.getCommissionsByTutel(this.id);
        apiCall.subscribe({
          next: (response: any) => {
            // Handle both single object and array responses
            let commissions: any[] = [];
            if (Array.isArray(response)) {
              commissions = response;
            } else if (response && typeof response === 'object') {
              // If it's a single object, wrap it in an array
              commissions = [response];
            } else if (response?.content) {
              commissions = response.content;
            } else if (response?.data) {
              commissions = response.data;
            }
            this.commissionOptions$.next(commissions.map((com: any) => ({ name: com.nom, code: com.id })));

            // If we have a current commission selection, ensure it's preserved
            if (currentComId && currentComSelected) {
              this.contractingAuthorityRepository.set('comSelected', currentComSelected);
            }

            this.updateFormObject();
          },
          error: () => {
            this.commissionOptions$.next([]);
            this.updateFormObject();
          },
        });
        break;
      case 'INTERNE': // Commission Interne
        apiCall = this.contractingAuthorityService.getPrivateCommissions(this.id);
        apiCall.subscribe({
          next: (response: any) => {
            // Handle both single object and array responses
            let commissions: any[] = [];
            if (Array.isArray(response)) {
              commissions = response;
            } else if (response && typeof response === 'object') {
              // If it's a single object, wrap it in an array
              commissions = [response];
            } else if (response?.content) {
              commissions = response.content;
            } else if (response?.data) {
              commissions = response.data;
            }
            this.commissionOptions$.next(commissions.map((com: any) => ({ name: com.nom, code: com.id })));

            // If we have a current commission selection, ensure it's preserved
            if (currentComId && currentComSelected) {
              this.contractingAuthorityRepository.set('comSelected', currentComSelected);
            }

            this.updateFormObject();
          },
          error: () => {
            this.commissionOptions$.next([]);
            this.updateFormObject();
          },
        });
        break;
      case 'AUTRE': // Autre Commission
        apiCall = this.contractingAuthorityService.getAllCommissions(this.id);
        apiCall.subscribe({
          next: (response: any) => {
            // Check if response is an array, if not, try to extract the data
            const commissions = Array.isArray(response) ? response : response?.content || response?.data || [];
            this.commissionOptions$.next(commissions.map((com: any) => ({ name: com.nom, code: com.id })));

            // If we have a current commission selection, ensure it's preserved
            if (currentComId && currentComSelected) {
              this.contractingAuthorityRepository.set('comSelected', currentComSelected);
            }

            this.updateFormObject();
          },
          error: () => {
            this.commissionOptions$.next([]);
            this.updateFormObject();
          },
        });
        break;
      default:
        this.commissionOptions$.next([]);
        this.updateFormObject();
    }
  }

  updateFormObject() {
    this.formObject = getContractingAuthorityFormObject({
      store: this.contractingAuthorityRepository,
      tuttelOptions: this.tuttelOptions,
      comOptions: this.comOptions,
      paysOptions: this.paysOptions,
      commissionTypeOptions: this.commissionTypeOptions,
      getCommissionOptions: () => this.commissionOptions$.value,
      onCommissionTypeChange: (commissionTypeId: string) => this.loadCommissionOptions(commissionTypeId),
      isCommissionTypeDisabled: true,
      isDetailsMode: true,
      contractingAuthorityService: this.contractingAuthorityService,
    });
  }

  createFormObject() {
    this.formObject = getContractingAuthorityFormObject({
      store: this.contractingAuthorityRepository,
      tuttelOptions: this.tuttelOptions,
      comOptions: this.comOptions,
      paysOptions: this.paysOptions,
      commissionTypeOptions: this.commissionTypeOptions,
      getCommissionOptions: () => this.commissionOptions$.value,
      onCommissionTypeChange: (commissionTypeId: string) => this.loadCommissionOptions(commissionTypeId),
      isCommissionTypeDisabled: true,
      isDetailsMode: true,
      contractingAuthorityService: this.contractingAuthorityService,
    });
  }

  loadCommissionOptionsAndCreateForm(commissionTypeId: string) {
    let apiCall: any;
    const currentComId = this.contractingAuthorityRepository.getNested<number>('dto', 'comId');
    const currentComSelected = this.contractingAuthorityRepository.get('comSelected');

    switch (commissionTypeId) {
      case 'TUTELLE': // Commission Tutelle
        apiCall = this.contractingAuthorityService.getCommissionsByTutel(this.id);
        apiCall.subscribe({
          next: (response: any) => {
            // Handle both single object and array responses
            let commissions: any[] = [];
            if (Array.isArray(response)) {
              commissions = response;
            } else if (response && typeof response === 'object') {
              // If it's a single object, wrap it in an array
              commissions = [response];
            } else if (response?.content) {
              commissions = response.content;
            } else if (response?.data) {
              commissions = response.data;
            }
            this.commissionOptions$.next(commissions.map((com: any) => ({ name: com.nom, code: com.id })));

            // Ensure commission selection is preserved
            if (currentComId && currentComSelected) {
              this.contractingAuthorityRepository.set('comSelected', currentComSelected);
            }

            this.createFormObject();
          },
          error: () => {
            this.commissionOptions$.next([]);
            this.createFormObject();
          },
        });
        break;
      case 'INTERNE': // Commission Interne
        apiCall = this.contractingAuthorityService.getPrivateCommissions(this.id);
        apiCall.subscribe({
          next: (response: any) => {
            // Handle both single object and array responses
            let commissions: any[] = [];
            if (Array.isArray(response)) {
              commissions = response;
            } else if (response && typeof response === 'object') {
              // If it's a single object, wrap it in an array
              commissions = [response];
            } else if (response?.content) {
              commissions = response.content;
            } else if (response?.data) {
              commissions = response.data;
            }
            this.commissionOptions$.next(commissions.map((com: any) => ({ name: com.nom, code: com.id })));

            // Ensure commission selection is preserved
            if (currentComId && currentComSelected) {
              this.contractingAuthorityRepository.set('comSelected', currentComSelected);
            }

            this.createFormObject();
          },
          error: () => {
            this.commissionOptions$.next([]);
            this.createFormObject();
          },
        });
        break;
      case 'AUTRE': // Autre Commission
        apiCall = this.contractingAuthorityService.getAllCommissionsForAdd();
        apiCall.subscribe({
          next: (response: any) => {
            // Check if response is an array, if not, try to extract the data
            const commissions = Array.isArray(response) ? response : response?.content || response?.data || [];
            this.commissionOptions$.next(commissions.map((com: any) => ({ name: com.nom, code: com.id })));

            // Ensure commission selection is preserved
            if (currentComId && currentComSelected) {
              this.contractingAuthorityRepository.set('comSelected', currentComSelected);
            }

            this.createFormObject();
          },
          error: () => {
            this.commissionOptions$.next([]);
            this.createFormObject();
          },
        });
        break;
      default:
        this.commissionOptions$.next([]);
        this.createFormObject();
    }
  }
}
